<template>
  <VaLayout
    :top="{ fixed: true, order: 2 }"
    :left="isGuard ? undefined : { fixed: true, absolute: breakpoints.mdDown, order: 1, overlay: breakpoints.mdDown && !isSidebarMinimized }"
    @leftOverlayClick="isSidebarMinimized = true"
  >
    <template #top>
      <AppNavbar :is-mobile="isMobile" :is-guard="isGuard" />
    </template>

    <template #left>
      <AppSidebar v-if="!isGuard" :minimized="isSidebarMinimized" :animated="!isMobile" :mobile="isMobile" />
    </template>

    <template #content>
      <div v-if="isGuard && route.name !== 'guard-home'" class="guard-backbar">
        <VaButton class="gk-homebtn" preset="secondary" icon="mso-home" @click="goHome">Home</VaButton>
      </div>
      <div :class="{ minimized: isSidebarMinimized }" class="app-layout__sidebar-wrapper">
        <div v-if="isFullScreenSidebar" class="flex justify-end">
          <VaButton class="px-4 py-4" icon="md_close" preset="plain" @click="onCloseSidebarButtonClick" />
        </div>
      </div>
      <AppLayoutNavigation v-if="!isMobile && !isGuard" class="p-4" />
      <main class="p-4 pt-0">
        <article>
          <RouterView :key="route.fullPath" />
        </article>
      </main>
    </template>
  </VaLayout>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { onBeforeRouteUpdate } from 'vue-router'
import { useBreakpoint } from 'vuestic-ui'

import { useGlobalStore } from '../stores/global-store'
import { useAuthStore } from '../stores/auth'

import AppLayoutNavigation from '../components/app-layout-navigation/AppLayoutNavigation.vue'
import AppNavbar from '../components/navbar/AppNavbar.vue'
import AppSidebar from '../components/sidebar/AppSidebar.vue'

const GlobalStore = useGlobalStore()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const isGuard = computed(() => authStore.user?.role === 'GUARD')
const goHome = () => router.push({ name: 'guard-home' })

const breakpoints = useBreakpoint()

const sidebarWidth = ref('16rem')
const sidebarMinimizedWidth = ref(undefined)

const isMobile = ref(false)
const isTablet = ref(false)
const { isSidebarMinimized } = storeToRefs(GlobalStore)

const onResize = () => {
  isSidebarMinimized.value = breakpoints.mdDown
  isMobile.value = breakpoints.smDown
  isTablet.value = breakpoints.mdDown
  sidebarMinimizedWidth.value = isMobile.value ? '0' : '4.5rem'
  sidebarWidth.value = isTablet.value ? '100%' : '16rem'
}

onMounted(() => {
  window.addEventListener('resize', onResize)
  onResize()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})

onBeforeRouteUpdate(() => {
  if (breakpoints.mdDown) {
    // Collapse sidebar after route change for Mobile
    isSidebarMinimized.value = true
  }
})

const isFullScreenSidebar = computed(() => isTablet.value && !isSidebarMinimized.value)

const onCloseSidebarButtonClick = () => {
  isSidebarMinimized.value = true
}
</script>

<style lang="scss" scoped>
// Prevent icon jump on animation
.va-sidebar {
  width: unset !important;
  min-width: unset !important;
}

.guard-backbar {
  padding: 0.85rem 1rem 0.35rem;
}
.gk-homebtn {
  min-height: 50px;
  padding: 0 22px;
  font-size: 1.05rem;
  font-weight: 700;
  border-radius: 999px;
  border: 2px solid var(--kapit-teal-500, #1d9e75);

  // Vuestic's preset="secondary" sets `border-color: transparent` as an inline
  // style (bound via :style on the root <button>), which beats any
  // class-selector rule regardless of specificity or :deep() — only
  // !important overrides an inline style, so it's required here.
  border-color: var(--kapit-teal-500, #1d9e75) !important;
}
</style>
